import { useState, useCallback } from 'react';
import {
  useDeleteManageAreaTaxLocationMutation,
  useUpdateManageAreaTaxLocationMutation,
} from '../../../services/ManageAreaTaxLocations/ManageAreaTaxLocationsService';

const useAreaTaxModals = () => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [locationToToggle, setLocationToToggle] = useState(null);

  const [showAreaTaxHistoryModal, setShowAreaTaxHistoryModal] = useState(false);
  const [selectedAreaTaxLocation, setSelectedAreaTaxLocation] = useState(null);
  const [stateObj, setStateObj] = useState({});

  const [expandedRows, setExpandedRows] = useState({});

  const [deleteAreaTaxLocation] = useDeleteManageAreaTaxLocationMutation();
  const [updateAreaTaxLocation] = useUpdateManageAreaTaxLocationMutation();

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  }, []);

  const closeSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const toggleExpandRow = useCallback((rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  }, []);

  const handleEditClick = useCallback((rowData) => {
    setSelectedRowData(rowData);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedRowData(null);
  }, []);

  const handleConfirmModal = useCallback(
    async (data) => {
      if (!data?.id) {
        showSnackbar('Invalid data provided', 'error');
        return;
      }
      try {
        await updateAreaTaxLocation({
          id: data.id,
          areaTaxLocationName: data.areaTaxLocationName || '',
        }).unwrap();
        setIsModalOpen(false);
        setSelectedRowData(null);
        showSnackbar('Area tax location updated successfully!');
      } catch (error) {
        console.error('Failed to update area tax location:', error);
        const errorMessage = error?.data?.message || 'Failed to update area tax location';
        showSnackbar(errorMessage, 'error');
      }
    },
    [updateAreaTaxLocation, showSnackbar],
  );

  const handleToggleStatus = useCallback((rowData) => {
    setLocationToToggle(rowData);
    setShowStatusModal(true);
  }, []);

  const handleConfirmStatusToggle = useCallback(async () => {
    if (!locationToToggle?.id) {
      setShowStatusModal(false);
      setLocationToToggle(null);
      return;
    }
    try {
      await deleteAreaTaxLocation(locationToToggle.id).unwrap();
      const newStatus = !locationToToggle.status;
      showSnackbar(`Area tax location ${newStatus ? 'activated' : 'deactivated'} successfully!`);
    } catch (error) {
      console.error('Failed to update status:', error);
      const errorMessage = error?.data?.message || 'Failed to update status';
      showSnackbar(errorMessage, 'error');
    }
    setShowStatusModal(false);
    setLocationToToggle(null);
  }, [locationToToggle, deleteAreaTaxLocation, showSnackbar]);

  const handleCancelStatusToggle = useCallback(() => {
    setShowStatusModal(false);
    setLocationToToggle(null);
  }, []);

  const handleOpenHistoryModal = useCallback((rowData) => {
    setSelectedAreaTaxLocation(rowData);
    setStateObj({
      stateId: rowData?.stateId || null,
      stateName: rowData?.stateName || '',
    });
    setShowAreaTaxHistoryModal(true);
  }, []);

  const handleCloseHistoryModal = useCallback(() => {
    setShowAreaTaxHistoryModal(false);
    setSelectedAreaTaxLocation(null);
  }, []);

  return {
    snackbar,
    showSnackbar,
    closeSnackbar,

    isModalOpen,
    selectedRowData,
    handleEditClick,
    handleCloseModal,
    handleConfirmModal,

    showStatusModal,
    locationToToggle,
    handleToggleStatus,
    handleConfirmStatusToggle,
    handleCancelStatusToggle,

    showAreaTaxHistoryModal,
    selectedAreaTaxLocation,
    stateObj,
    handleOpenHistoryModal,
    handleCloseHistoryModal,

    expandedRows,
    toggleExpandRow,
  };
};

export default useAreaTaxModals;
